/**
 * Room game lifecycle.
 *
 * Rooms never execute game-specific rules. They pin a published version,
 * record seating and seed, and relay an ordered action log to clients.
 */

import type { RoomStorage, BroadcastChannel, InternalRoomState } from '../../types/infrastructure.types'
import type { GameCatalogClient, ResolvedGameVersion, RoomCaller } from '../../types/room.types'
import { isHostMate } from '../hosting/hosting'
import type { StartGameArgs, MakeMoveArgs, ForceGameStateArgs } from '../../schemas/action.schemas'
import { serializeActiveGame } from '../publicState/publicState'

export interface GameInRoomContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
  gameCatalog: GameCatalogClient
}

const DEFAULT_GAME_CHANNEL = 'stable'

function shuffleInPlace<T>(items: T[]): void {
  for (let index = items.length - 1; index > 0; index--) {
    const swapIndex = crypto.getRandomValues(new Uint32Array(1))[0]! % (index + 1)
    ;[items[index], items[swapIndex]] = [items[swapIndex], items[index]]
  }
}

function generateSeed(): string {
  return Array.from(crypto.getRandomValues(new Uint32Array(4)), (value) => value.toString(36)).join('')
}

function resolveSeating(
  mates: Array<{ mateId: string }>,
  requestedMateIds: string[] | undefined,
  requestedGroups: string[][] | undefined,
  randomize: boolean | undefined,
): { mateIDs: string[]; mateSlotMap: Record<string, number>; mateGroups: string[][] } | { error: string } {
  const groups = requestedGroups?.map((group) => [...group])
  if (groups) {
    const seen = new Set<string>()
    for (const group of groups) {
      if (group.length === 0) return { error: 'Invalid mate groups: empty group' }
      for (const mateId of group) {
        if (seen.has(mateId)) return { error: 'Invalid mate groups: mate appears in multiple groups' }
        seen.add(mateId)
      }
    }
    if (randomize) shuffleInPlace(groups)
    return {
      mateIDs: groups.map((group) => group[0]!),
      mateGroups: groups,
      mateSlotMap: Object.fromEntries(groups.flatMap((group, slot) => group.map((mateId) => [mateId, slot]))),
    }
  }

  const mateIDs = requestedMateIds?.length ? [...requestedMateIds] : mates.map((mate) => mate.mateId)
  return {
    mateIDs,
    mateGroups: mateIDs.map((mateId) => [mateId]),
    mateSlotMap: Object.fromEntries(mateIDs.map((mateId, slot) => [mateId, slot])),
  }
}

function playerIdsFor(version: ResolvedGameVersion, playerCount: number): string[] {
  const configured = version.metadata.playerIds
  if (Array.isArray(configured)) return configured
  if (configured?.[playerCount]) return configured[playerCount]
  return Array.from({ length: playerCount }, (_, index) => String(index))
}

/**
 * A host action is accepted only in a game started in managed mode, from a
 * current host mate, submitted by the client that represents that mate.
 */
function hostActionError(state: InternalRoomState, mateId: string, caller: RoomCaller | undefined): string | null {
  if (!state.activeGame?.managed) return 'Host actions are only available in games started in a managed room'
  if (!isHostMate(state, mateId)) return 'Only hosts can make host actions'
  const mate = state.mates.find((m) => m.mateId === mateId)
  if (caller?.clientId && mate?.clientId !== caller.clientId) return "Cannot act for another client's mate"
  return null
}

export function makeGameInRoom(container: GameInRoomContainer) {
  const { storage, broadcastChannel, gameCatalog } = container

  const startGame = async (args: StartGameArgs): Promise<{ success: boolean; error?: string }> => {
    const state = storage.getState()
    if (state.activeGame) return { success: false, error: 'A game is already in progress' }

    const channel = args.channel ?? state.gameChannelPreferenceById?.[args.gameId] ?? DEFAULT_GAME_CHANNEL
    const gameVersion = await gameCatalog.resolveVersion(args.gameId, channel)
    if (!gameVersion) return { success: false, error: `No published game found for ${args.gameId} on ${channel}` }

    const seating = resolveSeating(state.mates, args.mateIDs, args.mateGroups, args.randomizeSlotAssignment)
    if ('error' in seating) return { success: false, error: seating.error }
    const playerCount = seating.mateGroups.length
    if (playerCount < gameVersion.metadata.minPlayers || playerCount > gameVersion.metadata.maxPlayers) {
      return { success: false, error: `This game supports ${gameVersion.metadata.minPlayers}-${gameVersion.metadata.maxPlayers} players` }
    }
    if (channel !== DEFAULT_GAME_CHANNEL && gameCatalog.subscribe) await gameCatalog.subscribe(args.gameId, channel)

    const activeGame = {
      gameId: args.gameId,
      mateIDs: seating.mateIDs,
      mateSlotMap: seating.mateSlotMap,
      mateGroups: seating.mateGroups,
      observerIDs: args.observerIDs,
      gameOptions: args.gameOptions,
      challengeConfiguration: args.challengeConfiguration,
      managed: Boolean(state.managed),
      playerIds: playerIdsFor(gameVersion, playerCount),
      startedAt: new Date().toISOString(),
      channel,
      gameVersion,
      seed: generateSeed(),
      actions: [],
    }
    storage.setState({ ...state, activeGame, gameSetup: null })
    broadcastChannel.broadcast({ type: 'gameStarted', data: { game: serializeActiveGame(activeGame)! } })
    return { success: true }
  }

  const endGame = async (): Promise<{ success: boolean }> => {
    const state = storage.getState()
    if (!state.activeGame) return { success: false }
    const endedGame = state.activeGame
    storage.setState({ ...state, activeGame: null })
    broadcastChannel.broadcast({ type: 'roomState', data: { activeGame: null, mates: state.mates } })
    broadcastChannel.broadcast({ type: 'gameEnded', data: {} })
    if (endedGame.channel !== DEFAULT_GAME_CHANNEL && gameCatalog.unsubscribe) {
      await gameCatalog.unsubscribe(endedGame.gameId, endedGame.channel!)
    }
    return { success: true }
  }

  const makeMove = async (args: MakeMoveArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
    const state = storage.getState()
    const activeGame = state.activeGame
    if (!activeGame) return { success: false, error: 'No active game' }
    if (!args.mateId) return { success: false, error: 'Mate id required for game moves', errorCode: 'MissingMateId' }
    if (!args.move || typeof args.move !== 'object' || !('type' in args.move) || typeof (args.move as { type?: unknown }).type !== 'string') {
      return { success: false, error: 'Invalid move format: missing type', errorCode: 'ValidationError' }
    }
    if (args.asHost) {
      const hostError = hostActionError(state, args.mateId, caller)
      if (hostError) return { success: false, error: hostError, errorCode: 'NotHost' }
    } else if (typeof activeGame.mateSlotMap?.[args.mateId] !== 'number') {
      return { success: false, error: 'Mate not in game', errorCode: 'InvalidMate' }
    }
    const actions = [...activeGame.actions, {
      type: 'move' as const,
      move: args.move as { type: string; args?: unknown[] }, mateId: args.mateId, stateHash: args.stateHash,
      ...(args.asHost ? { asHost: true } : {}),
    }]
    const updatedGame = { ...activeGame, actions }
    storage.setState({ ...state, activeGame: updatedGame })
    broadcastChannel.broadcast({ type: 'gameState', data: { game: serializeActiveGame(updatedGame)!, move: args.move, mateId: args.mateId, seqIndex: actions.length - 1 } })
    return { success: true }
  }

  /**
   * Append a platform-owned checkpoint without interpreting game state.
   *
   * `basedOnSeqIndex`, if provided, rejects the call unless the room's
   * current action-log length is exactly `basedOnSeqIndex + 1` — guards
   * against a checkpoint computed from a stale view silently clobbering a
   * move that arrived after it was computed but before it was submitted.
   */
  const forceGameState = async (args: ForceGameStateArgs): Promise<{ success: boolean; error?: string }> => {
    const state = storage.getState()
    const activeGame = state.activeGame
    if (!activeGame) return { success: false, error: 'No active game' }

    if (args.basedOnSeqIndex !== undefined && activeGame.actions.length !== args.basedOnSeqIndex + 1) {
      return { success: false, error: 'stale checkpoint' }
    }

    const actions = [...activeGame.actions, { type: 'forceGameState' as const, state: args.newState }]
    const updatedGame = { ...activeGame, actions }
    storage.setState({ ...state, activeGame: updatedGame })
    broadcastChannel.broadcast({
      type: 'stateForced',
      data: { game: serializeActiveGame(updatedGame)!, seqIndex: actions.length - 1 },
    })
    return { success: true }
  }

  const updateGameVersion = async (args: { gameId: string; channel: string; version: ResolvedGameVersion }): Promise<{ success: boolean }> => {
    const state = storage.getState()
    const activeGame = state.activeGame
    if (!activeGame || activeGame.gameId !== args.gameId || activeGame.channel !== args.channel) return { success: false }
    const updatedGame = { ...activeGame, gameVersion: args.version }
    storage.setState({ ...state, activeGame: updatedGame })
    broadcastChannel.broadcast({ type: 'gameVersionUpdated', data: { game: serializeActiveGame(updatedGame)! } })
    return { success: true }
  }

  const setActiveGameVersion = async (args: { gameId: string; version: ResolvedGameVersion }): Promise<{ success: boolean }> => {
    const state = storage.getState()
    const activeGame = state.activeGame
    if (!activeGame || activeGame.gameId !== args.gameId) return { success: false }
    const updatedGame = { ...activeGame, gameVersion: args.version }
    storage.setState({ ...state, activeGame: updatedGame })
    broadcastChannel.broadcast({ type: 'gameVersionUpdated', data: { game: serializeActiveGame(updatedGame)! } })
    return { success: true }
  }

  return { startGame, endGame, makeMove, forceGameState, updateGameVersion, setActiveGameVersion }
}
